import clsx from 'clsx';
import PropTypes from 'prop-types';
import React from 'react';

import Button from 'components/shared/button';
import CUSTOMER_ILLUSTRATIONS from 'constants/customer-illustrations';
import CheckIcon from 'icons/check.inline.svg';
import capterraIcon from 'images/logos/capterra.svg';
import gdprIcon from 'images/logos/gdpr.svg';
import soc2Icon from 'images/logos/soc2.svg';

const icons = {
  soc2: soc2Icon,
  capterra: capterraIcon,
  gdpr: gdprIcon,
};

const SectionWithTestimonialAndButton = (props) => {
  const {
    id,
    title,
    description,
    buttonText,
    buttonUrl,
    withBackground,
    isFeaturesTwoColumns,
    illustrationName,
    testimonialTitle,
    testimonial,
    featuresTitle,
    features,
    authorName,
    authorPosition,
    authorPhoto,
    banners,
  } = props;
  const withBanners = banners.length > 0;

  return (
    <section
      className={clsx(
        'safe-paddings relative scroll-mt-20 xl:py-30 lg:py-20 md:py-14',
        withBackground && 'bg-gray-1',
        withBanners ? 'pb-36 pt-[134px] lg:pt-0 md:pt-0' : 'pt-[10.5rem]'
      )}
      id={id || 'testimonial-section'}
    >
      <div className="container flex justify-between lg:flex-col lg:gap-y-10">
        <div className="max-w-[590px] shrink-0 xl:w-1/2 xl:pr-10 lg:w-full lg:pr-0 md:max-w-none">
          <h2 className="text-5xl font-bold leading-denser">{title}</h2>
          <div
            className="color-list with-link-primary-2 mt-4 max-w-[480px]"
            dangerouslySetInnerHTML={{ __html: description }}
          />
          {featuresTitle && (
            <h3 className="mt-16 text-2xl font-bold leading-normal lg:mt-6 md:text-xl">
              {featuresTitle}
            </h3>
          )}
          <ul
            className={clsx(
              'mt-5',
              isFeaturesTwoColumns && 'columns-2 gap-x-10 xl:gap-x-5 sm:columns-1'
            )}
          >
            {features.map((item, index) => (
              <li
                className="mt-4 text-lg leading-none first:mt-0 xl:text-base xl:leading-tight"
                key={index}
              >
                <CheckIcon
                  className="mr-4 inline-block w-6 fill-secondary-1 text-white xl:w-5 md:mr-1.5"
                  aria-hidden
                />
                {item.title}
              </li>
            ))}
          </ul>
          <Button
            className="mt-16 !h-12 px-16 lg:mt-6"
            to={buttonUrl}
            size="sm"
            theme="secondary-2"
            target="_blank"
          >
            {buttonText}
          </Button>
        </div>
        <div className="max-w-[490px] shrink-0 border-l border-gray-4 pl-18 xl:w-1/2 xl:pl-10 lg:w-full lg:border-none lg:pl-0">
          <div className="-ml-2 -mt-4 max-w-[267px]" aria-hidden>
            {CUSTOMER_ILLUSTRATIONS[illustrationName]}
          </div>
          <h3
            className="mt-5 font-sans text-lg lg:mt-3.5 [&>strong]:font-bold"
            dangerouslySetInnerHTML={{ __html: testimonialTitle }}
          />
          <figure>
            <blockquote
              className="mt-6 text-lg"
              dangerouslySetInnerHTML={{ __html: testimonial }}
            />
            <figcaption className="mt-6 flex items-center justify-start space-x-4 sm:space-x-2">
              <img
                className="h-12 rounded-full object-cover"
                src={authorPhoto.src || null}
                srcSet={authorPhoto.srcset || null}
                width={48}
                height={48}
                loading="lazy"
                alt={authorName}
              />
              <span className="shrink-0 text-base font-bold leading-none">{authorName}</span>
              <span className="!mx-0 text-base leading-none">, {authorPosition}</span>
            </figcaption>
          </figure>
        </div>
      </div>
      {withBanners && (
        <div className="container mt-[120px] border-t border-gray-4 lg:mt-20">
          <ul className="grid-gap-x mt-16 grid grid-cols-3 lg:block lg:space-y-8">
            {banners.map(({ iconName, title }, index) => (
              <li
                className="flex max-w-[384px] flex-col items-center rounded-[10px] border border-gray-4 p-8 lg:max-w-none"
                key={index}
              >
                <img
                  className="h-[100px]"
                  src={icons[iconName]}
                  height={100}
                  width={100}
                  alt={iconName}
                  loading="lazy"
                  aria-hidden
                />
                <h3 className="mt-5 text-xl font-bold leading-none md:mt-3.5">{title}</h3>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

SectionWithTestimonialAndButton.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  buttonUrl: PropTypes.string,
  withBackground: PropTypes.bool,
  featuresTitle: PropTypes.string,
  features: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
    })
  ),
  isFeaturesTwoColumns: PropTypes.bool,
  testimonialTitle: PropTypes.string.isRequired,
  testimonial: PropTypes.string.isRequired,
  illustrationName: PropTypes.oneOf(Object.keys(CUSTOMER_ILLUSTRATIONS)).isRequired,
  authorName: PropTypes.string.isRequired,
  authorPosition: PropTypes.string.isRequired,
  authorPhoto: PropTypes.shape({
    src: PropTypes.string,
    srcset: PropTypes.string,
  }).isRequired,
  banners: PropTypes.array,
};

SectionWithTestimonialAndButton.defaultProps = {
  id: '',
  buttonText: '',
  buttonUrl: '',
  featuresTitle: '',
  features: [],
  withBackground: false,
  isFeaturesTwoColumns: false,
  banners: [],
};

export default SectionWithTestimonialAndButton;
